/**
 * Warehouses Queries Service - TanStack Query Integration
 *
 * Este servicio encapsula todas las queries relacionadas con warehouses
 * (locations con type = WAREHOUSE) usando TanStack Query.
 *
 * Características:
 * - Cache automático con staleTime y gcTime configurables
 * - Reintentos automáticos en caso de error
 * - Invalidación de cache inteligente
 * - Signals reactivos para estado de loading/error/data
 *
 * @see https://tanstack.com/query/latest/docs/framework/angular/overview
 */

import { Injectable, Signal, inject } from '@angular/core';
import {
  injectQuery,
  injectMutation,
  injectQueryClient,
  CreateQueryResult,
  CreateMutationResult,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

// Services
import { LocationQueriesApiService } from '../services/location-queries-api.service';

// Models
import {
  LocationModel,
  LocationFilterModel,
  LocationListModel,
  LocationCreateModel,
  BackendSchedule,
  OperatingHours,
  DayOperationHours,
} from '@kanzi-apes/kanzi-models';

// Query Keys
import { locationsQueryKeys, buildWarehouseFilters } from './locations-query-keys';

@Injectable({
  providedIn: 'root',
})
export class WarehousesQueriesService {
  private apiService = inject(LocationQueriesApiService);
  private queryClient = injectQueryClient();

  // ==================== PRIVATE HELPERS ====================

  /**
   * Maps backend schedules array to operating_hours format
   * Converts from array format to day-keyed object format
   */
  private mapSchedulesToOperatingHours(schedules: BackendSchedule[]): OperatingHours {
    if (!schedules || schedules.length === 0) {
      return {};
    }

    const dayMap: { [key: number]: keyof OperatingHours } = {
      0: 'monday',
      1: 'tuesday',
      2: 'wednesday',
      3: 'thursday',
      4: 'friday',
      5: 'saturday',
      6: 'sunday',
    };

    const operatingHours: OperatingHours = {};

    schedules.forEach((schedule) => {
      const dayKey = dayMap[schedule.day_of_week];
      if (dayKey && !schedule.is_closed) {
        operatingHours[dayKey] = {
          open_time: schedule.open_time.substring(0, 5), // "09:00:00" → "09:00"
          close_time: schedule.close_time.substring(0, 5), // "21:00:00" → "21:00"
        };
      }
    });

    return operatingHours;
  }

  /**
   * Processes location data to map schedules to operating_hours
   */
  private processLocationData(location: LocationModel): LocationModel {
    if (location.schedules && location.schedules.length > 0) {
      location.operating_hours = this.mapSchedulesToOperatingHours(location.schedules);
    }
    return location;
  }

  // ==================== QUERIES ====================

  /**
   * Query para listar Warehouses (locations con type = WAREHOUSE)
   *
   * @param filters - Signal con filtros de búsqueda
   * @param enabled - Signal opcional que indica si la query está habilitada (default: true)
   * @returns CreateQueryResult con lista de warehouses
   *
   * @example
   * ```typescript
   * filters = signal<LocationFilterModel | null>({ page: 1, page_size: 50 });
   * isEnabled = signal(true);
   * warehousesQuery = this.queries.useWarehousesList(this.filters, this.isEnabled);
   *
   * // En template:
   * @if (warehousesQuery.isPending()) {
   *   <p-progressSpinner />
   * } @else if (warehousesQuery.data(); as warehouses) {
   *   <p-dataView [value]="warehouses.results"></p-dataView>
   * }
   * ```
   */
  useWarehousesList(
    filters: Signal<LocationFilterModel | null>,
    enabled?: Signal<boolean>
  ): CreateQueryResult<LocationListModel, Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.warehousesList(filters()),
      queryFn: async () => {
        // Construir filtros con type = WAREHOUSE
        const warehouseFilters = buildWarehouseFilters(filters());

        const result = await lastValueFrom(
          this.apiService.getLocationsList(warehouseFilters)
        );

        // Map schedules to operating_hours for each location
        if (result.results) {
          result.results = result.results.map((location) =>
            this.processLocationData(location)
          );
        }

        return result;
      },
      enabled: enabled ? enabled() : true,
      staleTime: 1000 * 60 * 5, // 5 minutos - warehouses cambian poco
      gcTime: 1000 * 60 * 30,   // 30 minutos
      retry: 1,
    }));
  }

  /**
   * Query para obtener detalle de un Warehouse
   *
   * @param warehouseId - Signal con el ID del warehouse
   * @param enabled - Signal que indica si la query está habilitada
   * @returns CreateQueryResult con detalle del warehouse
   *
   * @example
   * ```typescript
   * selectedWarehouseId = signal<number | null>(null);
   * isDetailEnabled = signal(false);
   *
   * warehouseQuery = this.queries.useWarehouseDetail(
   *   this.selectedWarehouseId,
   *   this.isDetailEnabled
   * );
   *
   * // Computed signal para null safety
   * warehouse = computed(() => {
   *   const isLoading = this.warehouseQuery.isPending() ||
   *                     this.warehouseQuery.isFetching();
   *   return isLoading ? null : this.warehouseQuery.data();
   * });
   * ```
   */
  useWarehouseDetail(
    warehouseId: Signal<number | null>,
    enabled: Signal<boolean>
  ): CreateQueryResult<LocationModel, Error> {
    return injectQuery(() => ({
      queryKey: warehouseId()
        ? locationsQueryKeys.warehouseDetail(warehouseId()!)
        : ['disabled'],
      queryFn: async () => {
        const id = warehouseId();
        if (!id) {
          throw new Error('Warehouse ID is required');
        }
        const location = await lastValueFrom(
          this.apiService.getLocationDetail(id)
        );
        return this.processLocationData(location);
      },
      enabled: enabled() && warehouseId() !== null,
      staleTime: 1000 * 60 * 2, // 2 minutos
      gcTime: 1000 * 60 * 30,
      retry: 1,
    }));
  }

  // ==================== MUTATIONS ====================

  /**
   * Mutation para crear un nuevo Warehouse
   *
   * @returns CreateMutationResult con funciones para ejecutar la mutación
   *
   * @example
   * ```typescript
   * createMutation = this.queries.useCreateWarehouse();
   *
   * onCreateWarehouse(warehouse: LocationCreateModel) {
   *   this.createMutation.mutate(warehouse, {
   *     onSuccess: (data) => {
   *       this.messageService.add({
   *         severity: 'success',
   *         summary: 'Warehouse creado',
   *         detail: `Warehouse ${data.name} creado exitosamente`
   *       });
   *       this.router.navigate(['warehouses', 'detail', data.id]);
   *     },
   *     onError: (error) => {
   *       console.error('Error creating warehouse:', error);
   *     }
   *   });
   * }
   *
   * // Verificar estado en template:
   * @if (createMutation.isPending()) {
   *   <p-progressSpinner />
   * }
   * ```
   */
  useCreateWarehouse(): CreateMutationResult<
    LocationModel,
    Error,
    LocationCreateModel,
    unknown
  > {
    return injectMutation(() => ({
      mutationFn: async (warehouse: LocationCreateModel) => {
        return lastValueFrom(this.apiService.createLocation(warehouse));
      },
      onSuccess: () => {
        // Invalidar lista de warehouses para recargar
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.warehouses(),
        });
      },
    }));
  }

  /**
   * Mutation para actualizar un Warehouse existente
   *
   * @returns CreateMutationResult con funciones para ejecutar la mutación
   *
   * @example
   * ```typescript
   * updateMutation = this.queries.useUpdateWarehouse();
   *
   * onUpdateWarehouse(warehouse: LocationCreateModel) {
   *   this.updateMutation.mutate(warehouse);
   * }
   * ```
   */
  useUpdateWarehouse(): CreateMutationResult<
    LocationModel,
    Error,
    LocationCreateModel,
    unknown
  > {
    return injectMutation(() => ({
      mutationFn: async (warehouse: LocationCreateModel) => {
        return lastValueFrom(this.apiService.updateLocation(warehouse));
      },
      onSuccess: (data) => {
        // Invalidar detalle del warehouse actualizado
        if (data.id) {
          this.queryClient.invalidateQueries({
            queryKey: locationsQueryKeys.warehouseDetail(data.id),
          });
        }

        // Invalidar lista para reflejar cambios
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.warehouses(),
        });
      },
    }));
  }

  /**
   * Mutation para eliminar un Warehouse
   *
   * @returns CreateMutationResult con funciones para ejecutar la mutación
   *
   * @example
   * ```typescript
   * deleteMutation = this.queries.useDeleteWarehouse();
   *
   * onDeleteWarehouse(id: number) {
   *   this.confirmService.confirm({
   *     message: '¿Está seguro de eliminar este warehouse?',
   *     accept: () => {
   *       this.deleteMutation.mutate(id);
   *     }
   *   });
   * }
   * ```
   */
  useDeleteWarehouse(): CreateMutationResult<
    LocationModel,
    Error,
    number,
    unknown
  > {
    return injectMutation(() => ({
      mutationFn: async (id: number) => {
        return lastValueFrom(this.apiService.deleteLocation(id));
      },
      onSuccess: () => {
        // Invalidar lista de warehouses
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.warehouses(),
        });
      },
    }));
  }

  /**
   * Mutation para carga masiva de Warehouses por archivo
   *
   * @returns CreateMutationResult para carga de archivo
   *
   * @example
   * ```typescript
   * uploadMutation = this.queries.useUploadWarehousesFile();
   *
   * onFileUpload(file: File) {
   *   const formData = new FormData();
   *   formData.append('file', file);
   *   formData.append('type', 'WAREHOUSE');
   *
   *   this.uploadMutation.mutate(formData);
   * }
   * ```
   */
  useUploadWarehousesFile(): CreateMutationResult<
    any,
    Error,
    FormData,
    unknown
  > {
    return injectMutation(() => ({
      mutationFn: async (formData: FormData) => {
        return lastValueFrom(
          this.apiService.uploadLocationsFile(formData)
        );
      },
      onSuccess: () => {
        // Invalidar lista completa
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.warehouses(),
        });
      },
    }));
  }

  // ==================== CACHE MANAGEMENT ====================

  /**
   * Invalida todo el cache relacionado con warehouses
   * Útil después de operaciones batch o cambios externos
   *
   * @example
   * ```typescript
   * // Después de importación masiva:
   * this.queries.invalidateAllWarehouses();
   * ```
   */
  invalidateAllWarehouses(): void {
    this.queryClient.invalidateQueries({
      queryKey: locationsQueryKeys.warehouses(),
    });
  }

  /**
   * Invalida el cache de un warehouse específico
   *
   * @param warehouseId - ID del warehouse
   *
   * @example
   * ```typescript
   * // Después de actualización externa:
   * this.queries.invalidateWarehouse(123);
   * ```
   */
  invalidateWarehouse(warehouseId: number): void {
    this.queryClient.invalidateQueries({
      queryKey: locationsQueryKeys.warehouseDetail(warehouseId),
    });
  }

  /**
   * Pre-carga los datos de un warehouse en el cache
   * Útil para mejorar UX antes de navegar al detalle
   *
   * @param warehouseId - ID del warehouse
   *
   * @example
   * ```typescript
   * // Al hacer hover sobre una card:
   * onWarehouseHover(warehouseId: number) {
   *   this.queries.prefetchWarehouse(warehouseId);
   * }
   * ```
   */
  async prefetchWarehouse(warehouseId: number): Promise<void> {
    await this.queryClient.prefetchQuery({
      queryKey: locationsQueryKeys.warehouseDetail(warehouseId),
      queryFn: () =>
        lastValueFrom(this.apiService.getLocationDetail(warehouseId)),
      staleTime: 1000 * 60 * 2,
    });
  }
}
